<template>
  <div class="memlist-tiptap" @click="focusEditor">
    <div class="editor-toolbar">
      <button @click="toggleBold" :class="{ active: isActive('bold') }">𝗕</button>
      <button @click="toggleItalic" :class="{ active: isActive('italic') }">𝑰</button>
      <button @click="toggleUnderline" :class="{ active: isActive('underline') }">U̲</button>
      <button @click="toggleStrike" :class="{ active: isActive('strike') }"><span style="text-decoration: line-through;">S</span></button>
      <button @click="toggleBlockquote" :class="{ active: isActive('blockquote') }">❝</button>
      <button @click="toggleLink" :class="{ active: isActive('link') }">🔗</button>
      <button @click="toggleCode" :class="{ active: isActive('code') }">⌘</button>
      <button @click="toggleHeading(1)" :class="{ active: isActive('heading', { level: 1 }) }"><span style="font-size: 10px;">H1</span></button>
      <button @click="toggleHeading(2)" :class="{ active: isActive('heading', { level: 2 }) }"><span style="font-size: 11px;">H2</span></button>
      <button @click="toggleHeading(3)" :class="{ active: isActive('heading', { level: 3 }) }"><span style="font-size: 13px;">H3</span></button>
      <button @click="toggleBulletList" :class="{ active: isActive('bulletList') }">• List</button>
      <button @click="toggleOrderedList" :class="{ active: isActive('orderedList') }">1. List</button>
      <button @click="setParagraph" :class="{ active: isActive('paragraph') }">P</button>
    </div>
    <editor-content ref="tiptapContent" v-if="editor" :editor="editor" class="tiptap-content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import Image from '@tiptap/extension-image'

Image.configure({
  allowBase64: true,
})

export default {
  name: 'MemlistTiptap',
  components: { EditorContent },
  props: { value: String },
  data() {
    return { editor: null }
  },
  watch: {
    value(val) {
      if (this.editor && val !== this.editor.getHTML()) {
        this.editor.commands.setContent(this.value || '')
      }
    }
  },
  mounted() {
    this.editor = new Editor({
      content: this.value || '',
      extensions: [
        StarterKit,
        Underline,
        Link,
        Image.configure({ allowBase64: true }),
      ],
      onUpdate: ({ editor }) => {
        this.$emit('input', editor.getHTML())
      }
    });

    
    // Paste handler for clipboard images
    document.addEventListener('paste', async (event) => {
      const items = (event.clipboardData || window.clipboardData).items
      if (!items) return

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          const reader = new FileReader()
          reader.onload = (e) => {
            this.editor.chain().focus().setImage({ src: e.target.result }).run()
          }
          reader.readAsDataURL(file)
          event.preventDefault()
          break
        }
      }
    });

    this.editor.commands.setContent(this.value || '')

  },

  beforeDestroy() {
    this.editor?.destroy()
  },
  methods: {
    focusEditor() {
      this.editor?.commands.focus()
    },
    isActive(type, attrs = {}) {
      return this.editor && this.editor.isActive(type, attrs)
    },
    toggleBold() { this.editor.chain().focus().toggleBold().run() },
    toggleItalic() { this.editor.chain().focus().toggleItalic().run() },
    toggleUnderline() { this.editor.chain().focus().toggleUnderline().run() },
    toggleStrike() { this.editor.chain().focus().toggleStrike().run() },
    toggleHeading(level) { this.editor.chain().focus().toggleHeading({ level }).run() },
    toggleBulletList() { this.editor.chain().focus().toggleBulletList().run() },
    toggleOrderedList() { this.editor.chain().focus().toggleOrderedList().run() },
    toggleBlockquote() { this.editor.chain().focus().toggleBlockquote().run() },
    toggleCode() { this.editor.chain().focus().toggleCode().run() },
    toggleLink() {
      const url = prompt('Enter URL')
      if (url) {
        this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
      }
    },
    setParagraph() { this.editor.chain().focus().setParagraph().run() },
  }
}
</script>

<style scoped>

.memlist-tiptap {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  padding: 0;
  overflow: hidden;
}

.tiptap-content {
  min-height: 250px;
  padding: 12px;
  cursor: text;
  white-space: pre-wrap;
  border: none;
  outline: none;
}



.tiptap-content p {
  margin: 0;
}
.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  padding: 8px 10px;
  gap: 8px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: inset 0 -1px 0 #e5e7eb;
}

.editor-toolbar button {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 8px;
  font-weight: 500;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
}

.editor-toolbar button:hover {
  background-color: #f1f5f9;
  border-color: #94a3b8;
}

.editor-toolbar button.active {
  background-color: #3b82f6;
  color: white;
  border-color: #2563eb;
}

.editor-toolbar button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4);
}

.tiptap-content:focus {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}

.tiptap-content:focus-visible {
  outline: none;
  box-shadow: none;
  border: 1px solid gray;
}

.tiptap-content h1,
.tiptap-content h2,
.tiptap-content h3 {
  font-weight: bold;
  margin: 1em 0 0.5em;
}


.tiptap-content ul,
.tiptap-content ol {
  padding-left: 1.5em;
  margin: 0.5em 0;
}

.tiptap-content blockquote {
  padding-left: 1em;
  border-left: 4px solid #ccc;
  color: #666;
  font-style: italic;
}

.tiptap-content .ProseMirror {
  outline: none !important;
  box-shadow: none !important;
  border: 1px solid #d1d5db !important;
  border-radius: 4px;
  padding: 12px;
  min-height: 250px;
  caret-color: #000; /* ensure cursor is visible */
}

.tiptap-content .ProseMirror:focus,
.tiptap-content .ProseMirror:focus-visible,
.tiptap-content .ProseMirror.ProseMirror-focused {
  outline: none !important;
  box-shadow: none !important;
  border: 1px solid #a0aec0 !important;
}


</style>
